'use memo'

import { useState } from 'react'
import { useFormikContext } from 'formik'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  PLUGIN_CORNERS_OPTIONS,
  PLUGIN_SPACING_OPTIONS,
  PLUGIN_SPACING_UNIT_RANGE,
  type PluginCorners,
  type PluginEmbedSettings,
} from '@sportninja/common/features/account/apiAccess'
import { SegmentedInput } from 'src/components/ts-components/SegmentedInput'
import { NumberField } from '../../../components/FormikField'
import {
  PLUGIN_BASE_SPACING_UNIT,
  PLUGIN_CUSTOM_SPACING_OPTION,
  type PluginSpacingChoice,
} from '../../constants/pluginSettings'
import { pluginSpacingChoiceOf } from '../../utils/pluginSpacingChoice'

const CORNER_OPTIONS = PLUGIN_CORNERS_OPTIONS.map(({ label, value }) => ({
  label: capitalizeWords(label),
  value,
}))

const SPACING_CHOICES: { label: string; value: PluginSpacingChoice }[] = [
  ...PLUGIN_SPACING_OPTIONS,
  PLUGIN_CUSTOM_SPACING_OPTION,
].map(({ label, value }) => ({ label: capitalizeWords(label), value }))

/** Corners and spacing. Rounded and Default are the plugin's own: no value. */
export const ThemeShape = () => {
  const { values, setFieldValue } = useFormikContext<PluginEmbedSettings>()
  const { corners, spacing } = values.branding
  // Custom writes nothing until a unit is typed, and stays picked when cleared.
  const [customOpen, setCustomOpen] = useState(typeof spacing === 'number')
  const spacingChoice = pluginSpacingChoiceOf(spacing, customOpen)

  const changeSpacing = (next: PluginSpacingChoice) => {
    setCustomOpen(next === 'custom')
    if (next === 'custom') {
      // From a named step: no unit yet, so nothing is written until one is typed.
      if (typeof spacing === 'string') {
        setFieldValue('branding.spacing', undefined)
      }
      return
    }
    setFieldValue('branding.spacing', next === 'default' ? undefined : next)
  }

  return (
    <>
      <SegmentedInput
        label={capitalizeWords('Corners')}
        options={CORNER_OPTIONS}
        value={corners ?? 'rounded'}
        onChange={(next: PluginCorners) =>
          setFieldValue(
            'branding.corners',
            next === 'rounded' ? undefined : next
          )
        }
      />
      <SegmentedInput
        label={capitalizeWords('Spacing')}
        options={SPACING_CHOICES}
        value={spacingChoice}
        onChange={changeSpacing}
      />
      {spacingChoice === 'custom' ? (
        <NumberField
          name='branding.spacing'
          label={capitalizeWords('Spacing Unit')}
          placeholder={PLUGIN_BASE_SPACING_UNIT}
          hint={`${PLUGIN_SPACING_UNIT_RANGE.min}–${PLUGIN_SPACING_UNIT_RANGE.max} px, in steps of ${PLUGIN_SPACING_UNIT_RANGE.step}`}
          emptyValue='unset'
        />
      ) : null}
    </>
  )
}
